import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { api } from '../../services/api'

interface AdminUser {
  id: string
  email: string
  name?: string
  role: string
  permissions?: string[]
}

interface AuthState {
  isAuthenticated: boolean
  loading: boolean
  error: string | null
  user: AdminUser | null
  accessToken: string | null
  refreshToken: string | null
  tokenExpiry: number | null
}

const initialState: AuthState = {
  isAuthenticated: false,
  loading: false,
  error: null,
  user: null,
  accessToken: null,
  refreshToken: null,
  tokenExpiry: null,
}

// Admin login using email/password
export const loginAdmin = createAsyncThunk(
  'auth/loginAdmin',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await api.post('/admin/login', {
      email,
      password,
    })
    
    // Store tokens in localStorage
    if (response.data.accessToken) {
      localStorage.setItem('admin_access_token', response.data.accessToken)
      localStorage.setItem('admin_refresh_token', response.data.refreshToken)
      localStorage.setItem('admin_email', email)
    }
    
    return response.data
  }
)

// Async thunk for token refresh
export const refreshAdminToken = createAsyncThunk(
  'auth/refreshToken',
  async () => {
    const refreshToken = localStorage.getItem('admin_refresh_token')
    if (!refreshToken) throw new Error('No refresh token available')
    
    const response = await api.post('/auth/refresh', {
      refreshToken,
    })
    
    localStorage.setItem('admin_access_token', response.data.accessToken)
    return response.data
  }
)

// Async thunk for admin logout
export const logoutAdmin = createAsyncThunk(
  'auth/logoutAdmin',
  async () => {
    localStorage.removeItem('admin_access_token')
    localStorage.removeItem('admin_refresh_token')
    localStorage.removeItem('admin_email')
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload
    },
    setUser: (state, action: PayloadAction<AdminUser>) => {
      state.user = action.payload
    },
    clearAuth: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      state.tokenExpiry = null
      state.error = null
    },
    restoreAuth: (state) => {
      const accessToken = localStorage.getItem('admin_access_token')
      const refreshToken = localStorage.getItem('admin_refresh_token')
      const email = localStorage.getItem('admin_email')
      
      if (accessToken && refreshToken) {
        state.accessToken = accessToken
        state.refreshToken = refreshToken
        
        // Decode JWT to get user info (simplified - in production use jwt-decode)
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]))
          state.user = {
            id: payload.sub || '',
            email: email || payload.email || '',
            name: payload.name || '',
            role: payload.adminRole || payload.role || 'admin',
            permissions: payload.permissions || [],
          }
          state.tokenExpiry = payload.exp * 1000 // Convert to milliseconds
          
          // Only set authenticated to true if token decode succeeds
          state.isAuthenticated = true
        } catch (error) {
          console.error('Failed to decode token:', error)
          // Clear invalid tokens
          localStorage.removeItem('admin_access_token')
          localStorage.removeItem('admin_refresh_token')
          localStorage.removeItem('admin_email')
          // Ensure authentication is false
          state.isAuthenticated = false
          state.accessToken = null
          state.refreshToken = null
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login admin
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.accessToken = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken
        state.tokenExpiry = action.payload.expiresIn
        state.user = action.payload.adminUser || action.payload.user || {
          id: action.payload.userId || '',
          email: action.payload.email || localStorage.getItem('admin_email') || '',
          name: action.payload.name || '',
          role: action.payload.role || 'admin',
          permissions: action.payload.permissions || [],
        }
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Authentication failed'
        state.isAuthenticated = false
      })
      
      // Refresh token
      .addCase(refreshAdminToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken
        state.tokenExpiry = action.payload.expiresIn
      })
      .addCase(refreshAdminToken.rejected, (state) => {
        state.isAuthenticated = false
        state.accessToken = null
        state.refreshToken = null
        state.user = null
      })
      
      // Logout
      .addCase(logoutAdmin.fulfilled, (state) => {
        state.isAuthenticated = false
        state.user = null
        state.accessToken = null
        state.refreshToken = null
        state.tokenExpiry = null
      })
  },
})

export const { setAuthenticated, setUser, clearAuth, restoreAuth } = authSlice.actions
export default authSlice.reducer